import { EditSquare, InvoiceEdit } from 'seagull/icons';
import { fromRelationshipFundingSource, labelOfSetPaymentMethod, matchRelationshipPaymentMethodCell } from '~/business-logic/paymentMethodCellLogic';
import { relationshipSummaryModel } from '~/models';
import { HeaderColId } from './AgreementsDashboard.utils';
export const agreementColumnNames = (shape)=>[
        'companyName',
        'status',
        ...shape.isVendor ? [
            'assignee'
        ] : [],
        ...shape.showLastModifiedBy && shape.isVendor ? [
            'lastModifiedBy'
        ] : [],
        'effectiveDate',
        'paymentMethod',
        'lastBilled'
    ];
export const agreementColumnToHeaderId = {
    companyName: HeaderColId.companyName,
    status: HeaderColId.status,
    assignee: HeaderColId.assignee,
    lastModifiedBy: HeaderColId.lastModifiedBy,
    effectiveDate: HeaderColId.effectiveDate,
    paymentMethod: HeaderColId.paymentMethod,
    lastBilled: HeaderColId.lastBilled
};
export const achOrCardLabel = (cellState, paymentMethodTexts)=>labelOfSetPaymentMethod(cellState, {
        ach: (last4)=>paymentMethodTexts.achWithLast4({
                last4
            }),
        creditCard: (last4)=>paymentMethodTexts.creditCardWithLast4({
                last4
            })
    });
export const verificationInitiatedText = (fundingSource, paymentMethodTexts)=>fundingSource.isPendingAutomaticVerification() ? paymentMethodTexts.automaticVerificationInitiated() : paymentMethodTexts.manualVerificationInitiated();
export const paymentMethodText = (param)=>{
    let { agreement, paymentMethodTexts } = param;
    return matchRelationshipPaymentMethodCell(fromRelationshipFundingSource(agreement.fundingSource), {
        set: (cellState)=>achOrCardLabel(cellState, paymentMethodTexts),
        pendingDetails: ()=>paymentMethodTexts.pendingDetails(),
        notSet: ()=>''
    });
};
export const paymentMethodSubtitle = (param)=>{
    let { agreement, dashboardTexts, paymentMethodTexts } = param;
    var _agreement_fundingSource, _agreement_fundingSource1;
    if ((_agreement_fundingSource = agreement.fundingSource) === null || _agreement_fundingSource === void 0 ? void 0 : _agreement_fundingSource.failedVerification()) return dashboardTexts.verificationFailed();
    if (!((_agreement_fundingSource1 = agreement.fundingSource) === null || _agreement_fundingSource1 === void 0 ? void 0 : _agreement_fundingSource1.isPendingMicroDeposit())) return undefined;
    return verificationInitiatedText(agreement.fundingSource, paymentMethodTexts);
};
export const agreementStatusIndicators = (param)=>{
    let { agreement, showAmendmentDraft, texts } = param;
    return [
        ...agreement.hasAmendments ? [
            {
                icon: InvoiceEdit,
                tooltip: texts.awaitingApproval()
            }
        ] : [],
        ...showAmendmentDraft && agreement.hasAmendmentDraft ? [
            {
                icon: EditSquare,
                tooltip: texts.amendmentDraft()
            }
        ] : []
    ];
};
export const AGREEMENT_STATUS_BADGE_SKIN = {
    [relationshipSummaryModel.AgreementStatus.Active]: 'success',
    [relationshipSummaryModel.AgreementStatus.Exhausted]: 'default',
    [relationshipSummaryModel.AgreementStatus.Terminated]: 'error'
};
export const agreementStatusLabel = (status, texts)=>{
    if (status === relationshipSummaryModel.AgreementStatus.Active) return texts.active();
    if (status === relationshipSummaryModel.AgreementStatus.Exhausted) return texts.completed();
    if (status === relationshipSummaryModel.AgreementStatus.Terminated) return texts.terminated();
    return status;
};
const toAgreementRow = (param)=>{
    let { agreement, isVendor, texts } = param;
    var _relationshipSummaryModel_agreementStatus, _relationshipSummaryModel_sellerFullName, _ref, _agreement_effectiveDate, _agreement_lastBilledDate;
    var _relationshipSummaryModel_lastModificationRecord;
    const status = (_relationshipSummaryModel_agreementStatus = relationshipSummaryModel.agreementStatus(agreement)) !== null && _relationshipSummaryModel_agreementStatus !== void 0 ? _relationshipSummaryModel_agreementStatus : '';
    return {
        id: agreement.id,
        companyName: relationshipSummaryModel.opposingPartyName(agreement, isVendor),
        status: status ? agreementStatusLabel(status, texts.statusLabels) : '',
        assignee: (_relationshipSummaryModel_sellerFullName = relationshipSummaryModel.sellerFullName(agreement)) !== null && _relationshipSummaryModel_sellerFullName !== void 0 ? _relationshipSummaryModel_sellerFullName : '',
        lastModifiedBy: (_ref = (_relationshipSummaryModel_lastModificationRecord = relationshipSummaryModel.lastModificationRecord(agreement)) === null || _relationshipSummaryModel_lastModificationRecord === void 0 ? void 0 : _relationshipSummaryModel_lastModificationRecord.savedByName) !== null && _ref !== void 0 ? _ref : '',
        effectiveDate: (_agreement_effectiveDate = agreement.effectiveDate) !== null && _agreement_effectiveDate !== void 0 ? _agreement_effectiveDate : undefined,
        paymentMethod: paymentMethodText({
            agreement,
            paymentMethodTexts: texts.paymentMethod
        }),
        lastBilled: (_agreement_lastBilledDate = agreement.lastBilledDate) !== null && _agreement_lastBilledDate !== void 0 ? _agreement_lastBilledDate : undefined
    };
};
export const toAgreementRows = (param)=>{
    let { agreements, isVendor, texts } = param;
    return agreements.map((agreement)=>toAgreementRow({
            agreement,
            isVendor,
            texts
        }));
};
export const agreementStatusSkins = (param)=>{
    let { agreements, texts } = param;
    return new Map(agreements.map((agreement)=>{
        var _relationshipSummaryModel_agreementStatus, _AGREEMENT_STATUS_BADGE_SKIN_status;
        const status = (_relationshipSummaryModel_agreementStatus = relationshipSummaryModel.agreementStatus(agreement)) !== null && _relationshipSummaryModel_agreementStatus !== void 0 ? _relationshipSummaryModel_agreementStatus : '';
        return [
            agreementStatusLabel(status, texts),
            (_AGREEMENT_STATUS_BADGE_SKIN_status = AGREEMENT_STATUS_BADGE_SKIN[status]) !== null && _AGREEMENT_STATUS_BADGE_SKIN_status !== void 0 ? _AGREEMENT_STATUS_BADGE_SKIN_status : 'default'
        ];
    }));
};
export const agreementMainColumn = (param)=>{
    let { texts, subtitle } = param;
    return {
        type: 'text',
        name: 'companyName',
        header: texts.companyName(),
        width: {
            initial: 200,
            min: 200
        },
        weight: 'normal',
        subtitle,
        sortable: true,
        bi: {
            name: 'company_name'
        }
    };
};
export const agreementColumns = (param)=>{
    let { texts, shape, effectiveDateLabel, statusSkin, statusInfo, statusIndicators, lastModifiedSubtitle, paymentMethodInfo, paymentMethodSubtitleOf } = param;
    return [
        {
            type: 'badge',
            name: 'status',
            header: texts.status(),
            width: {
                initial: 128,
                min: 128
            },
            sortable: true,
            bi: {
                name: 'status'
            },
            useColor: statusSkin,
            useInfo: statusInfo,
            indicators: statusIndicators
        },
        ...shape.isVendor ? [
            {
                type: 'text',
                name: 'assignee',
                header: texts.assignee(),
                width: {
                    initial: 130,
                    min: 130
                },
                sortable: true,
                bi: {
                    name: 'assignee'
                }
            }
        ] : [],
        ...shape.showLastModifiedBy && shape.isVendor ? [
            {
                type: 'text',
                name: 'lastModifiedBy',
                header: texts.lastModifiedBy(),
                width: {
                    initial: 130,
                    min: 130
                },
                subtitle: lastModifiedSubtitle
            }
        ] : [],
        {
            type: 'date',
            name: 'effectiveDate',
            header: effectiveDateLabel,
            width: {
                initial: 128,
                min: 128
            },
            sortable: true,
            bi: {
                name: 'effective_date'
            }
        },
        {
            type: 'text',
            name: 'paymentMethod',
            header: texts.paymentMethod(),
            width: {
                initial: 180,
                min: 180
            },
            subtitle: paymentMethodSubtitleOf,
            info: paymentMethodInfo,
            sortable: true,
            bi: {
                name: 'payment_method'
            }
        },
        {
            type: 'date',
            name: 'lastBilled',
            header: texts.lastBilled(),
            width: {
                initial: 100,
                min: 100
            },
            sortable: true,
            bi: {
                name: 'last_billed'
            }
        }
    ];
};
export const agreementMobileFields = (param)=>{
    let { effectiveDateLabel, paymentMethodLabel } = param;
    return {
        fields: [
            {
                name: 'status',
                inline: true
            },
            {
                name: 'effectiveDate',
                label: effectiveDateLabel
            },
            {
                name: 'paymentMethod',
                label: paymentMethodLabel
            }
        ]
    };
};
export const agreementSortColIndex = (param)=>{
    let { name, shape } = param;
    return agreementColumnNames(shape).indexOf(name);
};
export const agreementSortName = (param)=>{
    let { colIndex, shape } = param;
    var _agreementColumnNames_colIndex;
    return (_agreementColumnNames_colIndex = agreementColumnNames(shape)[colIndex]) !== null && _agreementColumnNames_colIndex !== void 0 ? _agreementColumnNames_colIndex : 'companyName';
};
